// frontend/src/components/homepage/OpportunitySection.js
//
// "This is what an opportunity looks like" - the head grammar of ProseSection
// with a short row of REAL hero lead cards underneath.
//
// IT REUSES LeadCardLive, IT DOES NOT COPY IT. The card is the one element of
// the homepage that shows rather than describes what the product does, and a
// second, drifting copy of it on the profession pages would be the first thing
// to look wrong after the next design round. LeadCardLive is already
// presentational-only - it owns no timer, no cycle and no animation, it renders
// whatever lead and score it is handed - so a still card is exactly what it
// does when nobody drives it. Nothing here imports useHeroCycle.
//
// STILL, NOT CYCLING. `score` is passed as the lead's own figure, so the card
// mounts finished: the number reads its final value and the bar is drawn to it
// (LeadCardLive's bar width is `score > 0 ? lead.score : 0`, and the hero passes
// 0 there only because its cycle is about to count up). No refs are handed down,
// so nothing can write to the card after mount.
//
// THE REVEAL IS ON A WRAPPER, NEVER ON THE CARD. `.hl-lead` carries its own
// `transform`/`opacity` transition (the hero's file-out), and `hl-reveal`
// carries another; on one element the two fight over `transform` and the winner
// is whichever the browser composites last. Same split TypesLive documents for
// its tilt.
//
// NO CAROUSEL, NO DOTS. The hero already owns the moving deck; this section is
// the still spread a reader stops at, and it stacks to one column under 900px -
// the same breakpoint the blog strip and the phone hero key on.

import React from "react";
import ProseSection from "./ProseSection";
import LeadCardLive from "./LeadCardLive";
import useReveal from "../../motion/useReveal";
import { HERO_HOT_SCORE } from "./heroData";
import "../../professionLanding.css";

/**
 * The two labels the card itself renders, verbatim from HeroLive's own COPY
 * constant (they are not exported from there, and this file must not reach into
 * another section's internals to read them).
 *
 * They are props with these as defaults, so a content module can override them
 * without touching this file - but the homepage's wording is what ships.
 */
export const CARD_COPY = {
  he: { scoreLabel: "ציון AI", hotStamp: "HOT LEAD" },
  en: { scoreLabel: "AI score", hotStamp: "HOT LEAD" },
};

/**
 * The card wrapper's styles, as a MODULE CONSTANT.
 *
 * LeadCardLive is `React.memo`'d and `sx` is one of its props: an object
 * literal in the render body would be a new reference every render and the memo
 * would never hit. HeroLive states the same rule over its own button styles.
 */
const CARD_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "430px",
  marginInline: "auto",
};

/** The stagger between card entrances, the blog strip's `.1s` step. */
export const CARD_STAGGER_SECONDS = 0.1;

/**
 * One card in the row: a revealing slot with a still lead card inside it.
 */
function OpportunityCard({ lead, hotScore, scoreLabel, hotStamp, delay }) {
  const reveal = useReveal({ threshold: 0.1 });
  const classes = reveal.className ? `hl-pl-opp-slot ${reveal.className}` : "hl-pl-opp-slot";

  return (
    <div ref={reveal.ref} className={classes} style={delay ? { "--hl-d": delay } : undefined}>
      <LeadCardLive
        lead={lead}
        score={lead.score}
        hot={lead.score >= hotScore}
        scoreLabel={scoreLabel}
        hotStamp={hotStamp}
        sx={CARD_SX}
      />
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} [props.id] anchor id.
 * @param {string} [props.eyebrow]
 * @param {string} props.title the section's single H2.
 * @param {string|string[]} [props.body] optional prose between the H2 and the row.
 * @param {Array<object>} props.cards leads in `heroData.js` shape
 *   (`pro`/`grp`/`txt`/`city`/`time`/`score`). Two or three; more than three
 *   would need a second row the design does not have.
 * @param {number} [props.hotScore] at or above which a card stamps HOT. The
 *   HERO's threshold, not the ticker's - this is a hero card in a still row.
 * @param {boolean} [props.isHebrew]
 * @param {string} [props.scoreLabel]
 * @param {string} [props.hotStamp]
 * @param {string} [props.scene] SceneWashes key.
 * @param {string} [props.mesh]
 * @param {string|number} [props.meshAlpha]
 */
export default function OpportunitySection({
  id,
  eyebrow,
  title,
  body,
  cards = [],
  hotScore = HERO_HOT_SCORE,
  isHebrew = true,
  scoreLabel,
  hotStamp,
  scene = "types",
  mesh = "disperse",
  meshAlpha = ".25",
}) {
  const copy = isHebrew ? CARD_COPY.he : CARD_COPY.en;
  // A section with no cards renders its head and nothing else rather than an
  // empty grid: a content module mid-edit must not leave a bare gap on the page.
  if (!cards.length) {
    return (
      <ProseSection
        id={id}
        eyebrow={eyebrow}
        title={title}
        body={body}
        scene={scene}
        mesh={mesh}
        meshAlpha={meshAlpha}
        className="hl-pl-opp"
      />
    );
  }

  return (
    <ProseSection
      id={id}
      eyebrow={eyebrow}
      title={title}
      body={body}
      scene={scene}
      mesh={mesh}
      meshAlpha={meshAlpha}
      className="hl-pl-opp"
    >
      {/* `data-count` rather than a length-derived inline grid-template: the two
          column counts this row supports are a design decision with a phone
          breakpoint attached, and that belongs in the sheet. */}
      <div className="hl-pl-opp-grid" data-count={String(cards.length)}>
        {cards.map((lead, index) => (
          <OpportunityCard
            key={`${index}:${lead.pro}`}
            lead={lead}
            hotScore={hotScore}
            scoreLabel={scoreLabel || copy.scoreLabel}
            hotStamp={hotStamp || copy.hotStamp}
            delay={index === 0 ? undefined : `${index * CARD_STAGGER_SECONDS}s`}
          />
        ))}
      </div>
    </ProseSection>
  );
}
